/**
 * LE GRAPHE D'UNE PAGE COMMANDE & CONTRÔLE, hors du rendu.
 *
 * La page montre en direct ce que les mesures cochées retirent d'un score
 * ventilé par catégorie (l'eau par usage, la pollution par polluant…). Elle en
 * montrait un seul, `score_in_doughnut`, dans un anneau ; elle peut maintenant
 * en montrer deux (`chart_scores`), sous trois formes (`chart_display`).
 *
 * LES PAGES D'AVANT N'ONT RIEN À CHANGER : sans `chart_scores`, le graphe est
 * `score_in_doughnut` seul, et sans `chart_display`, un anneau. Même règle que
 * `game_validator.chart_score_ids`, à garder d'accord.
 *
 * LA VENTILATION EST RECALCULÉE depuis la liste des mesures cochées, pas
 * incrémentée clic par clic. L'ancienne page bornait à zéro en ajoutant et pas
 * en retirant : cocher puis décocher une mesure qui vidait une catégorie
 * laissait la catégorie plus haute qu'au départ.
 */

import { CATEGORICAL } from '../graph_pages/graph_components/chartTheme';

export const CHART_DISPLAYS = [
  { id: 'doughnut', label: 'Anneau' },
  { id: 'half_doughnut', label: 'Demi-anneau' },
  { id: 'bars', label: 'Barres' },
];
export const DEFAULT_CHART_DISPLAY = 'doughnut';
export const MAX_CHART_SCORES = 2;
// La seule unité que la page connaissait : un score sans `unit` la garde.
export const DEFAULT_UNIT = 'm³';

/** Les scores que le graphe montre, dans l'ordre (doublons retirés, deux au plus). */
export function chartScoreIdsOf(content) {
  const declared = content?.chart_scores;
  const ids = Array.isArray(declared) && declared.some(Boolean)
    ? declared.filter(Boolean)
    : [content?.score_in_doughnut].filter(Boolean);
  return [...new Set(ids)].slice(0, MAX_CHART_SCORES);
}

/** La forme du graphe ; une valeur inconnue retombe sur l'anneau. */
export function chartDisplayOf(content) {
  const display = content?.chart_display;
  return CHART_DISPLAYS.some((d) => d.id === display) ? display : DEFAULT_CHART_DISPLAY;
}

/** Un score peut-il être dessiné ? Il lui faut sa ventilation de départ et d'arrivée. */
export function isChartable(score) {
  return Boolean(score?.metadata?.standard && score?.metadata?.current);
}

/**
 * Les séries du graphe, prêtes à dessiner. Un score qui ne peut pas l'être
 * est passé au lieu de planter la page — le validateur l'a déjà dit à
 * l'auteur (`score_in_doughnut_missing_metadata`).
 *
 * `colorOffset` décale la palette du second graphe : sans lui, la première
 * catégorie de chacun porterait le même bleu, et une couleur ne dirait plus
 * une seule chose sur l'écran.
 */
export function chartSeriesOf(content, scoreDic) {
  let offset = 0;
  return chartScoreIdsOf(content)
    .filter((scoreId) => isChartable(scoreDic?.[scoreId]))
    .map((scoreId) => {
      const score = scoreDic[scoreId];
      const categories = Object.keys(score.metadata.current);
      const series = {
        scoreId,
        name: score.name || scoreId,
        icon: score.icon || '',
        unit: score.unit || DEFAULT_UNIT,
        categories,
        base: { ...score.metadata.current },
        // La référence du pourcentage, comme avant : la valeur standard, à
        // défaut la somme de la ventilation standard.
        standardTotal: Number(score.standard_value) || sum(Object.values(score.metadata.standard)),
        colorOffset: offset,
      };
      offset += categories.length;
      return series;
    });
}

/** La ventilation d'une série une fois les mesures cochées appliquées. */
export function breakdownWith(series, selectedDecisions) {
  const next = { ...series.base };
  (selectedDecisions || []).forEach((decision) => {
    const detail = decision?.score?.[series.scoreId]?.metadata || {};
    Object.entries(detail).forEach(([category, delta]) => {
      if (Object.prototype.hasOwnProperty.call(next, category)) {
        next[category] += Number(delta) || 0;
      }
    });
  });
  Object.keys(next).forEach((category) => {
    next[category] = Math.max(next[category], 0);
  });
  return next;
}

/**
 * Ce que les mesures cochées retirent du score — la somme des impacts, comme
 * l'ancienne page, et non l'écart de ventilation : une mesure peut porter un
 * impact sans ventilation, et le pourcentage annoncé doit rester celui d'avant.
 */
export function savingsWith(series, selectedDecisions) {
  return (selectedDecisions || []).reduce(
    (acc, decision) => acc - (Number(decision?.score?.[series.scoreId]?.impact) || 0),
    0,
  );
}

/** La part retirée, en pourcentage arrondi de la valeur de départ. */
export function savingsPercent(series, savings) {
  if (!series.standardTotal) return 0;
  return Math.round((savings / series.standardTotal) * 100);
}

/** La couleur d'une catégorie d'une série, d'après son rang et le décalage de la série. */
export function categoryColor(series, index) {
  return CATEGORICAL[(series.colorOffset + index) % CATEGORICAL.length];
}

/** Un nombre en milliers séparés par des espaces, comme le centre de l'anneau. */
export function formatAmount(value) {
  return Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

function sum(values) {
  return values.reduce((acc, v) => acc + (Number(v) || 0), 0);
}
